<template>
  <div class="television-package-card io-eq-el">
    <div
        class="television-package-card__title"
        data-eq-part="television-package-card__title"
    >
      <div class="television-package-card__title-name">{{ package.name }}</div>
      <div class="television-package-card__title-slogan">{{ package.slogan }}</div>
    </div>
    <div
        class="television-package-card__benefits"
        data-eq-part="television-package-card__benefits"
    >
      <List
          type="icon"
          icon="fa-solid fa-check"
          :items="packageProConList"
      />
    </div>
    <div class="television-package-card__price" data-eq-part="television-package-card__price">
      <div>{{ monthlyBasePrice }}&thinsp;€/Monat</div>
      <div>{{ includesLabel }}</div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import List from '@/components/List.vue';
import { formatNumber } from '@/helpers';
import TelevisionPackageInterface from '@/interfaces/TelevisionPackageInterface';
import { computed } from 'vue';
import TelevisionProducts from '../../../public/TelevisionProducts.json';

const props = defineProps<{
  package: TelevisionPackageInterface
}>();

const allPackages = TelevisionProducts.products as TelevisionPackageInterface[];
const monthlyBasePrice = computed(() => formatNumber(props.package.price?.recurring.find((recurringPrice) => recurringPrice?.type === 'base_price')?.value || 0, 2));
const includesLabel = computed((): string | undefined => {
  const includedProducts = allPackages.filter((pckg) => (props.package.price?.includes || []).includes(pckg.id));
  let includedLabel = '';

  includedProducts.forEach((pckg, inx) => {
    if (inx === 0) {
      includedLabel = `${includedLabel} das ${pckg.name}`;
    } else if (inx === includedProducts.length - 1) {
      includedLabel = `${includedLabel} und das ${pckg.name}`;
    } else {
      includedLabel = `${includedLabel}, das ${pckg.name}`;
    }
  });

  if (includedLabel.length) return `inkludiert ${includedLabel}`;

  return undefined;
});

const packageProConList = computed(() => [
  ...(props.package.benefits || []),
  ...((props.package.cons || []).map((con: string | string[]) => [
    Array.isArray(con) ? con[0] : con,
    Array.isArray(con) ? con[1] : undefined,
    'fa-solid fa-xmark',
  ])),
]);
</script>

<style lang="scss">
@import "src/scss/component-helpers";

.television-package-card {
  box-shadow: 0 3px 6px rgba(0, 0, 0, .15);
  border-radius: 20px;
  background-color: #ffffff;

  @media(min-width: map-get($grid-breakpoints, 'lg')) {
    border-radius: 40px;
  }

  &.selected {
    position: relative;

    &:after {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: 20px;
      border: 2px solid #2B257E;
      pointer-events: none;
      z-index: 4;

      @media(min-width: map-get($grid-breakpoints, 'lg')) {
        border-radius: 40px;
      }
    }
  }

  &.disabled {
    position: relative;

    &:after {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: 20px;
      background-color: rgba(255, 255, 255, .7);
      z-index: 4;

      @media(min-width: map-get($grid-breakpoints, 'lg')) {
        border-radius: 40px;
      }
    }
  }

  &__title {
    display: grid;
    grid-template-columns: auto;
    justify-content: center;
    text-align: center;
    padding: 20px 20px 15px;
    align-items: center;
    border-bottom: 1px solid #ECF2F6;

    @media(min-width: map-get($grid-breakpoints, 'lg')) {
      padding: 30px 40px 20px;
      justify-content: start;
      text-align: left;
    }

    &-name {
      font-size: 1.25rem;
      font-weight: 700;
      line-height: 1.5;

      @media(min-width: map-get($grid-breakpoints, 'lg')) {
        font-size: 1.875rem;
      }
    }

    &-slogan {
      font-size: .875rem;
      font-weight: 400;
      line-height: 1.5;

      @media(min-width: map-get($grid-breakpoints, 'lg')) {
        font-size: 1.25rem;
      }

      &:empty {
        display: none;
      }
    }
  }

  &__benefits {
    text-align: left;
    padding: 20px 20px;

    @media(min-width: map-get($grid-breakpoints, 'lg')) {
      padding: 20px 40px;
    }
  }

  &__price {
    padding: 10px 20px;
    background-color: #ECF2F6;
    text-align: center;
    font-size: 1.25rem;
    font-weight: 700;
    line-height: 1.5;
    border-radius: 0 0 20px 20px;
    min-height: 3.75rem;
    display: grid;
    grid-template-columns: 100%;
    align-items: center;
    justify-content: center;
    gap: 0;

    @media(min-width: map-get($grid-breakpoints, 'lg')) {
      font-size: 1.875rem;
      border-radius: 0 0 40px 40px;
      padding: 10px 40px;
      min-height: 5.625rem;
    }

    > div {
      &:empty {
        display: none;
      }

      &:nth-child(2) {
        font-size: .75rem;
        font-weight: 300;

        @media(min-width: map-get($grid-breakpoints, 'lg')) {
          font-size: .875rem;
        }
      }
    }
  }
}
</style>
